import { DetailList, formatDateTime, PageError, PageLoading } from "@majitap/ui";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { useQuery } from "@tanstack/react-query";
import { api, errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { answerText, isFileKind } from "../../deliveries";
import { messageFor } from "../../error-copy";
import styles from "./record.module.css";

/**
 * Who took the delivery and the driver's answers to the proof checklist: checks, readings, notes,
 * photos and signatures (links last a few minutes; an image opens full size). From the order page and the delivery's page (P8-48).
 */
export function ProofDialog({
  orderId,
  delivery,
  onClose,
}: {
  orderId: string;
  delivery: { id: string; delivery_number: string };
  onClose: () => void;
}) {
  const format = useCompanyFormat();
  const proof = useQuery({
    queryKey: ["proof", delivery.id],
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.GET("/v1/admin/orders/{id}/deliveries/{delivery_id}/proof", {
        params: { path: { id: orderId, delivery_id: delivery.id } },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={`Proof of delivery: ${delivery.delivery_number}`}>
        {proof.status === "pending" ? (
          <PageLoading label="Loading the proof" />
        ) : proof.status === "error" ? (
          errorOf(proof.error).code === "PROOF_NOT_RECORDED" ? (
            <p className={styles.muted}>The driver hasn't recorded the proof of delivery yet.</p>
          ) : (
            <PageError message={messageFor(errorOf(proof.error))} onRetry={() => proof.refetch()} />
          )
        ) : (
          <div className={styles.stackGap}>
            <DetailList
              items={[
                { label: "Received by", value: proof.data.received_by_name },
                { label: "Signed", value: proof.data.signed_at ? formatDateTime(proof.data.signed_at, format) : null },
                ...proof.data.checklist
                  .filter((a) => !isFileKind(a.kind))
                  .map((a) => ({ label: a.label, value: answerText(a) })),
              ]}
            />
            {proof.data.checklist.map((a) =>
              a.file_url ? (
                <figure key={a.item_id} className={styles.stackGap}>
                  <a href={a.file_url} target="_blank" rel="noreferrer">
                    <img
                      src={a.file_url}
                      alt={`${a.label}, ${delivery.delivery_number}`}
                      className={styles.proofImage}
                    />
                  </a>
                  <figcaption className={styles.muted}>{a.label}</figcaption>
                </figure>
              ) : null,
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
